import { useCallback } from "react";
import {
  postChatMessage,
  postChatMessageStream,
  getChatSession as getSession,
  archiveChatSession as apiArchiveChatSession,
  putResponseRating,
  getChatHistory,
  type ChatStreamOptions,
} from "../api/Chat";
import { AnswerFormatter } from "../helpers/answerFormatter";
import { buildChatSession } from "../helpers/chatSessionBuilders";
import { ChatSession, Exchange, Message } from "../interfaces/Chat";
import _ from "lodash";
import { GetChatHistoryRes } from "../models/Chat";
import type { FeedbackCategoryId } from "@common/domain/types";
import { UserInfo } from "../contexts/UserInfoContext";
import { Persona } from "../api/Persona";
import { stripExtraTags } from "@common/domain/tag-filter";
import { hydrateExchangeMetadata } from "../helpers/chatMetadataRestore";

export const useChat = () => {
  const postMessage = useCallback(async (
    message: string,
    userInfo: UserInfo,
    chatId: string,
    publicationFromYear: number | null = null,
    publicationToYear: number | null = null,
  ) => {
    const result = await postChatMessage(
      message,
      chatId,
      userInfo.accessToken,
      publicationFromYear,
      publicationToYear
    );
    return result;
  }, []);

  const postMessageStream = useCallback(async (
    message: string,
    userInfo: UserInfo,
    chatId: string,
    persona?: Persona,
    conversationHistory?: Array<{ role: 'user' | 'assistant'; content: string }>,
    signal?: AbortSignal,
    streamOptions?: ChatStreamOptions
  ) => {
    const result = await postChatMessageStream(
      message,
      chatId,
      userInfo.accessToken,
      persona,
      conversationHistory,
      signal,
      streamOptions
    );
    return result;
  }, []);

  const getChatSession = useCallback(async (userInfo: UserInfo, chatId: string, userId?: string) => {
    const res = await getSession(userInfo, chatId, userId);
    return res;
  }, []);

  const getChatMessages = useCallback(async (userInfo: UserInfo, chatId: string) => {
    const res = await getChatSession(userInfo, chatId);
    if (res) {
      let messages: Message[] = [];
      const conversation = res.conversation || [];

      // Use forEach instead of for...in for arrays to ensure correct iteration
      conversation.forEach((rawExchange: Exchange) => {
        if (!rawExchange) return;
        const exchange = hydrateExchangeMetadata(rawExchange);

        // Ensure we have a message content, even if field names vary
        const question = exchange.question || "";
        const response = exchange.response || (exchange as any).result || "";

        const isComparisonChild = Boolean(res.isComparisonChild || exchange.parentChatId);

        messages.push({
          message: question,
          fromUser: true,
          exchangeId: exchange.exchangeId || (exchange as any).exchange_id || `user-${crypto.randomUUID()}`,
          safetyRoute: exchange.safetyRoute,
          sources: {},
          responseMode: exchange.responseMode,
          toolTrace: exchange.toolTrace,
          goal: exchange.goal,
          useSynapse: exchange.useSynapse,
          comparisonRunId: exchange.comparisonRunId,
          comparisonStreamId: exchange.comparisonStreamId,
          comparisonStreamLabel: exchange.comparisonStreamLabel,
          streamChatId: res.chatId,
          settings: exchange.comparisonSettings,
        });

        messages.push({
          message: AnswerFormatter(response, exchange.sources),
          fromUser: false,
          exchangeId: exchange.exchangeId || (exchange as any).exchange_id || `assistant-${crypto.randomUUID()}`,
          sources: exchange.sources || {},
          seeds: exchange.seeds || [],
          retrievedChunkIds: exchange.retrievedChunkIds || [],
          usedChunkIds: exchange.usedChunkIds || [],
          safetyRoute: exchange.safetyRoute,
          rating: exchange.rating,
          comment: exchange.comment,
          // Carried through so an avatar turn reviewed in chatbot mode still
          // offers the avatar failure categories, and keeps ones already chosen.
          feedbackMode: exchange.feedbackMode,
          feedbackCategory: exchange.feedbackCategory,
          responseMode: exchange.responseMode,
          workflowMetadata: exchange.workflowMetadata,
          safetyClassification: exchange.safetyClassification,
          retrievalDecision: exchange.retrievalDecision,
          conversationMomentum: exchange.conversationMomentum,
          alignment: exchange.alignment,
          retrievalSummary: exchange.retrievalSummary,
          toolTrace: exchange.toolTrace,
          goal: exchange.goal,
          useSynapse: exchange.useSynapse,
          comparisonRunId: exchange.comparisonRunId,
          comparisonStreamId: exchange.comparisonStreamId,
          comparisonStreamLabel: exchange.comparisonStreamLabel,
          streamChatId: res.chatId,
          isVariant: isComparisonChild,
          settings: exchange.comparisonSettings,
        });
      });
      return messages;
    }
    return undefined;
  }, [getChatSession]);

  const createNewChatSession = useCallback((title?: string, personaId?: string) => {
    return buildChatSession(title, personaId);
  }, []);

  const archiveChatSession = useCallback(async (userInfo: UserInfo, chatId: string) => {
    const res = await apiArchiveChatSession(chatId, userInfo.accessToken);
    return res;
  }, []);

  const putRating = useCallback(async (
  chatId: string,
  exchangeId: string,
  rating: number | null,
  comment?: string,
  accessToken?: string,
  personaId?: string,
  categories?: FeedbackCategoryId[]
) => {
  const result = await putResponseRating(
    chatId,
    exchangeId,
    rating,
    comment,
    accessToken,
    personaId,
    categories
  );
  return result;
}, []);


  const getUpdatedSelectedChatSession = useCallback((
    chatId: string,
    chatSessions: ChatSession[]
  ) => {
    let newlySelectedChatSessionId = "";
    if (chatSessions.length === 1) return newlySelectedChatSessionId;
    const deletedChatSessionIndex = _.findIndex(
      chatSessions,
      function (chatSession) {
        return chatSession.chatInfo.chatId === chatId;
      }
    );
    if (deletedChatSessionIndex === chatSessions.length - 1) {
      newlySelectedChatSessionId =
        chatSessions[deletedChatSessionIndex - 1].chatInfo.chatId;
    } else
      newlySelectedChatSessionId =
        chatSessions[deletedChatSessionIndex + 1].chatInfo.chatId;

    return newlySelectedChatSessionId;
  }, []);

  const getChatSessionArray = useCallback((result: GetChatHistoryRes) => {
    const chatSessionsArray: ChatSession[] = [];
    if (_.isEmpty(result["monthlyChatSessions"])) {
      return chatSessionsArray;
    } else {
      const monthlyChatSessions = result["monthlyChatSessions"];
      for (const monthYear in monthlyChatSessions) {
        if (monthlyChatSessions.hasOwnProperty(monthYear)) {
          const sessions = monthlyChatSessions[monthYear];
          if (Array.isArray(sessions)) {
            sessions.forEach((session) => {
              // Ensure chatId exists, fallback to id if necessary (legacy support)
              const chatId = session.chatId || (session as any).id;
              if (!chatId) return;

              const chatSession: ChatSession = {
                month: monthYear,
                chatInfo: {
                  ...session,
                  chatId: chatId,
                  chatTitle: stripExtraTags(session.chatTitle || "Untitled Conversation")
                },
              };
              chatSessionsArray.push(chatSession);
            });
          }
        }
      }
      return chatSessionsArray.sort((a, b) => {
        const dateA = a.chatInfo.timestamp ? new Date(a.chatInfo.timestamp).getTime() : 0;
        const dateB = b.chatInfo.timestamp ? new Date(b.chatInfo.timestamp).getTime() : 0;
        return dateB - dateA;
      });
    }
  }, []);

  const getHistory = useCallback(async (userInfo: UserInfo, personaId?: string) => {
    const result = await getChatHistory(userInfo, personaId);
    if (!result) return result;
    const chatSessionArray = getChatSessionArray(result);
    return chatSessionArray;
  }, [getChatSessionArray]);

  return {
    postMessage,
    postMessageStream,
    getChatSession,
    getChatMessages,
    createNewChatSession,
    archiveChatSession,
    putRating,
    getUpdatedSelectedChatSession,
    getHistory,
  };
};
